import type { ConsentFormTemplate } from '@lmc/common/types'
import type { Dispatch, SetStateAction } from 'react'
import { useEffect, useRef, useState } from 'react'

/**
 * Manages the two template state slices used by ConsentFormNew:
 *
 * - `copyOfTemplates`   — the working copy the user edits.
 * - `originalTemplates` — the canonical baseline used by `isMatchingFormBody`
 *                         to detect whether a form body was changed.
 *
 * Handles three cases that must all coexist:
 *
 * 1. Sync open: templates already populated when the modal opens — seed both
 *    immediately.
 * 2. Async open: templates arrive empty on open, then hydrate — seed both once
 *    they arrive.
 * 3. Inline-filter churn: the parent re-creates the `templates` array reference
 *    on every render — do NOT overwrite `copyOfTemplates` (would destroy edits).
 *
 * `originalTemplatesInitRef` guards against using a stale `originalTemplates`
 * value from a prior modal session. A ref is used rather than a functional-
 * updater length check because a functional updater can resolve `prevOriginals`
 * from React's committed state before a same-batch `setOriginalTemplates([])`
 * call (from the first-open branch) has been applied.
 *
 * When the modal closes, both template slices are reset to `[]` so
 * `copyOfTemplates` cannot carry a prior session into the next open; otherwise
 * the async-hydration branch (`prev.length === 0`) would never run if `prev`
 * stayed non-empty while new `templates` arrived after an empty first paint.
 */
export function useTemplateSeeding(
  isModalConsentVisible: boolean,
  templates: ConsentFormTemplate[]
): {
  copyOfTemplates: ConsentFormTemplate[]
  setCopyOfTemplates: Dispatch<SetStateAction<ConsentFormTemplate[]>>
  originalTemplates: ConsentFormTemplate[]
} {
  const [copyOfTemplates, setCopyOfTemplates] = useState<ConsentFormTemplate[]>(
    []
  )
  const [originalTemplates, setOriginalTemplates] = useState<
    ConsentFormTemplate[]
  >([])

  const modalWasOpenRef = useRef(false)
  const originalTemplatesInitRef = useRef(false)

  useEffect(() => {
    if (!isModalConsentVisible) {
      modalWasOpenRef.current = false
      originalTemplatesInitRef.current = false
      setCopyOfTemplates([])
      setOriginalTemplates([])
      return
    }

    if (!modalWasOpenRef.current) {
      setCopyOfTemplates(templates)
      setOriginalTemplates(templates)
      originalTemplatesInitRef.current = templates.length > 0
      modalWasOpenRef.current = true
      return
    }

    setCopyOfTemplates((prev) => {
      if (prev.length === 0 && templates.length > 0) {
        return templates
      }
      return prev
    })
    if (!originalTemplatesInitRef.current && templates.length > 0) {
      setOriginalTemplates(templates)
      originalTemplatesInitRef.current = true
    }
  }, [isModalConsentVisible, templates])

  return { copyOfTemplates, setCopyOfTemplates, originalTemplates }
}
